import { proxy } from "valtio"
import type { TicketedItemFragment } from "../../types.graphql"
import { Spektrix } from "../../server/lib/spektrix-types"
import {
  eventTickets,
  selectableTicketPrices,
  selectionSummary,
  type TicketPricing,
  type TicketSelection,
} from "./ticket-selection"

export type TicketSelectionRequest = {
  event: TicketedItemFragment
  spektrixEventIds: number[]
  selectedSession?: {
    eventId: number
    sessionId: number
  }
}

export type CheckoutStep = {
  label: string
  key: "none" | "tickets" | "seats" | "payment"
}

export type CheckoutStepKey = CheckoutStep["key"]

const BASKET_EXPAND = "tickets/event,tickets/instance,tickets/type,membershipSubscriptions/membership"

function createCheckoutStore() {
  let basketBaseUrl: string | undefined
  let initialLoad: Promise<boolean> | undefined
  let selectionRevision = 0
  // Counts cart operations, so a background read can tell when it's been overtaken by one.
  let basketRevision = 0
  let backgroundRefresh: Promise<void> | undefined

  async function readBasket(client: Spektrix.Client, signal: AbortSignal) {
    const basket = await client.GET("v3/basket", { expand: BASKET_EXPAND }, { signal })
    signal.throwIfAborted()
    store.basket = basket
    return basket
  }

  async function deleteTickets(client: Spektrix.Client, ticketIds: string[], signal: AbortSignal) {
    // The Web API only removes the first repeated ticketIds query value.
    for (const ticketId of new Set(ticketIds)) {
      signal.throwIfAborted()
      await client.DELETE("v3/basket/tickets?ticketIds={ticketIds}", { ticketIds: [ticketId] }, { signal })
    }
  }

  async function withBasket(baseUrl: string, action: (client: Spektrix.Client, signal: AbortSignal) => Promise<void>) {
    if (store.isPending) return false
    basketBaseUrl = baseUrl
    basketRevision++
    const operation = new AbortController()
    store.isPending = true
    const client = Spektrix.createClient({ baseUrl, credentials: "include" })
    try {
      await action(client, operation.signal)
      return !operation.signal.aborted
    } catch (error) {
      if (!operation.signal.aborted) {
        if (error instanceof Spektrix.HttpError) {
          for (const limit of error.instanceLimits) store.instanceLimits[limit.instanceId] = limit.maxTickets
        }
        alert(error instanceof Error ? error.message : "Your cart could not be updated. Please try again.")
      }
      return false
    } finally {
      store.isPending = false
    }
  }

  const store = proxy({
    isOpen: false,
    isPending: false,
    basket: null as Spektrix.Basket | null,
    instanceLimits: {} as Record<string, number>,
    ticketSelectionRequest: null as TicketSelectionRequest | null,
    step: "tickets" as CheckoutStepKey,
    async loadBasket(baseUrl: string) {
      if (!initialLoad) {
        initialLoad = withBasket(baseUrl, async (client, signal) => {
          await readBasket(client, signal)
        })
      }
      return initialLoad
    },
    async refreshBasket() {
      if (!basketBaseUrl) return false
      return withBasket(basketBaseUrl, async (client, signal) => {
        await readBasket(client, signal)
      })
    },
    /** Re-reads the basket without marking the cart busy or reporting failures. */
    refreshBasketInBackground() {
      if (!basketBaseUrl || store.isPending) return
      if (backgroundRefresh) return backgroundRefresh
      const revision = basketRevision
      const client = Spektrix.createClient({ baseUrl: basketBaseUrl, credentials: "include" })
      backgroundRefresh = client
        .GET("v3/basket", { expand: BASKET_EXPAND })
        .then((basket) => {
          // A cart operation that started meanwhile reads its own basket, so this one may be stale.
          if (revision !== basketRevision) return
          // Keep the current snapshot when nothing changed, so the cart doesn't re-render.
          if (JSON.stringify(basket) !== JSON.stringify(store.basket)) store.basket = basket
        })
        .catch(() => {
          // Keep the last known basket. The next refresh or cart action will try again.
        })
        .finally(() => {
          backgroundRefresh = undefined
        })
      return backgroundRefresh
    },
    async removeTickets(ticketIds: string[]) {
      if (!basketBaseUrl || !ticketIds.length) return false
      return withBasket(basketBaseUrl, async (client, signal) => {
        try {
          await deleteTickets(client, ticketIds, signal)
        } finally {
          // Keep the summary accurate even if only some deletions succeeded.
          if (!signal.aborted) await readBasket(client, signal)
        }
        if (store.basket?.tickets.some((ticket) => ticketIds.includes(ticket.id))) {
          throw new Error("Your tickets could not be removed. Please try again.")
        }
      })
    },
    async removeMembership(subscriptionId: string) {
      if (!basketBaseUrl || !subscriptionId) return false
      return withBasket(basketBaseUrl, async (client, signal) => {
        try {
          await client.DELETE("v3/basket/membership-subscriptions/{subscriptionId}", { subscriptionId }, { signal })
        } finally {
          if (!signal.aborted) await readBasket(client, signal)
        }
        if (store.basket?.membershipSubscriptions.some((subscription) => subscription.id === subscriptionId)) {
          throw new Error("Your membership could not be removed. Please try again.")
        }
      })
    },
    async openMembershipPurchase(membershipId: string) {
      if (!basketBaseUrl || !membershipId || store.isPending) return false
      const revision = ++selectionRevision
      const saved = await withBasket(basketBaseUrl, async (client, signal) => {
        if (!store.basket) throw new Error("Your cart has not loaded yet. Please reload before continuing.")
        // Spektrix allows only one subscription of each membership type in a basket.
        if (store.basket.membershipSubscriptions.some((subscription) => subscription.membership.id === membershipId)) {
          return
        }
        try {
          await client.POST("v3/basket/membership-subscriptions", [{ membership: membershipId, autoRenew: false }], {
            signal,
          })
        } finally {
          // Membership benefits can also change the prices of tickets already in the cart.
          if (!signal.aborted) await readBasket(client, signal)
        }
        if (!store.basket.membershipSubscriptions.some((subscription) => subscription.membership.id === membershipId)) {
          throw new Error("Your membership could not be confirmed in your cart. Please reload before continuing.")
        }
      })
      if (!saved || revision !== selectionRevision) return false
      store.openCheckout()
      return true
    },
    async replaceTickets(config: TicketPricing, selection: TicketSelection) {
      const revision = selectionRevision
      const saved = await withBasket(config.webApiBaseUrl, async (client, signal) => {
        if (!store.basket) throw new Error("Your cart has not loaded yet. Please reload before continuing.")
        const existing = eventTickets(config, store.basket.tickets)
        const summary = selectionSummary(
          config,
          selection,
          existing.filter((ticket) => ticket.instance.id === config.instanceId).length,
          store.instanceLimits[config.instanceId] ?? null,
          existing.length > 0,
        )
        if (!summary.isValidSelection) {
          throw new Error(summary.validityWarning ?? "Please choose a valid ticket selection.")
        }
        const tickets = selectableTicketPrices(config).flatMap((price) =>
          Array.from({ length: selection[price.id] ?? 0 }, () => ({
            instance: config.instanceId,
            seatingPlan: config.plan.id,
            type: price.ticketType.id,
          })),
        )
        let returned: Spektrix.Ticket[] = []
        try {
          await deleteTickets(
            client,
            existing.map((ticket) => ticket.id),
            signal,
          )
          // The response contains all basket tickets, including other events.
          if (tickets.length) returned = await client.POST("v3/basket/tickets", tickets, { signal })
        } finally {
          // Refresh once after the complete change, including partial failures.
          if (!signal.aborted) await readBasket(client, signal)
        }
        const retained = new Set(store.basket.tickets.map((ticket) => ticket.id))
        if (returned.some((ticket) => !retained.has(ticket.id))) {
          throw new Error("Your tickets could not be confirmed in your cart. Please reload before continuing.")
        }
      })
      // Finish the basket update if the dialog closes, without advancing a newer selection.
      return saved && revision === selectionRevision
    },
    openTicketSelection(req: TicketSelectionRequest) {
      selectionRevision++
      store.ticketSelectionRequest = req
      store.isOpen = true
      store.step = "tickets"
    },
    openCheckout() {
      selectionRevision++
      store.ticketSelectionRequest = null
      store.isOpen = true
      store.step = "payment"
    },
    close() {
      selectionRevision++
      store.isOpen = false
      store.step = "none"
    },
    goToStep(step: CheckoutStepKey) {
      store.step = step
    },
    get steps(): CheckoutStep[] {
      const steps: CheckoutStep[] = []

      if (store.ticketSelectionRequest) {
        steps.push({
          key: "tickets",
          label: "Tickets",
        })
        steps.push({
          key: "payment",
          label: "Payment",
        })
        return steps
      }

      return [
        {
          key: "payment",
          label: "Complete your purchase",
        },
      ]
    },
  })

  return store
}

export const checkoutStore = createCheckoutStore()

export type CheckoutStore = typeof checkoutStore
